import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Play, Plus, Trash2, Zap } from "lucide-react";
import AutomationBuilder, { emptyAutomation } from "./AutomationBuilder";
import MessageTemplates from "./MessageTemplates";
import RunLog from "./RunLog";
import WebhookEndpoints from "./WebhookEndpoints";
import { errorText } from "./common";

// Workflow Automation Center: automations, run log, message templates and
// outgoing webhooks. Master Administrator only (the backend enforces it).
// `TemplateApprovals` is the reduced screen a Sales Manager uses to review
// the messages automations are allowed to send.

const TABS = [["automations", "Automations"], ["runs", "Run log"], ["templates", "Message templates"], ["webhooks", "Webhooks"]];

export function TemplateApprovals({ api, showNotice, user }) {
  return (
    <div className="page-stack" data-testid="automation-approvals">
      <section className="hero-panel ceo-hero glass-panel"><div><p className="eyebrow">Sales Manager / Master Admin</p><h2>Automation message approvals</h2></div></section>
      <MessageTemplates api={api} showNotice={showNotice} user={user} />
    </div>
  );
}

export default function AutomationCenter({ api, showNotice, user }) {
  const [tab, setTab] = useState("automations");
  const [automations, setAutomations] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [list, cat] = await Promise.all([api.get("/automations"), api.get("/automations/catalog")]);
      setAutomations(list.data.automations || []);
      setCatalog(cat.data || {});
    } catch (err) {
      showNotice(errorText(err, "Failed to load automations"), "error");
    }
  }, [api, showNotice]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (auto) => {
    try { await api.post(`/automations/${auto.id}/toggle`, { data: { enabled: !auto.enabled } }); showNotice(`Automation ${auto.enabled ? "paused" : "enabled"}`, "success"); await load(); } catch (err) { showNotice(errorText(err, "Could not change the automation"), "error"); }
  };
  const remove = async (auto) => {
    if (!window.confirm(`Delete "${auto.name}"? Waiting runs are cancelled.`)) return;
    try { await api.delete(`/automations/${auto.id}`); showNotice("Automation deleted", "success"); await load(); } catch (err) { showNotice(errorText(err, "Could not delete"), "error"); }
  };

  const eventLabel = (type) => (catalog.events || []).find((e) => e.type === type)?.label || type;

  return (
    <div className="page-stack" data-testid="workflow-automation-center">
      <section className="hero-panel ceo-hero glass-panel">
        <div>
          <p className="eyebrow">Master Admin Only • Triggers, conditions, delays, messages and webhooks</p>
          <h2>Workflow Automation Center</h2>
          <div className="hero-stats"><span>{automations.filter((a) => a.enabled).length} active</span><span>{automations.length} total</span></div>
        </div>
        <button className="primary-btn" data-testid="automation-create-button" onClick={() => { setTab("automations"); setEditing(emptyAutomation()); }}><Zap size={16} /> Create Automation</button>
      </section>

      <div className="compact-row" role="tablist">
        {TABS.map(([id, label]) => <button key={id} role="tab" aria-selected={tab === id} className={tab === id ? "primary-btn small" : "ghost-btn small"} data-testid={`automation-tab-${id}`} onClick={() => setTab(id)}>{label}</button>)}
      </div>

      {tab === "automations" && (
        <>
          {editing && <AutomationBuilder api={api} showNotice={showNotice} catalog={catalog} initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
          <section className="panel glass-panel">
            <div className="panel-header"><h3>Automations</h3><button className="ghost-btn small" onClick={() => setEditing(emptyAutomation())}><Plus size={14} /> New</button></div>
            <div className="compact-list">
              {automations.map((auto) => (
                <div key={auto.id} className="compact-row" data-testid={`automation-${auto.id}`}>
                  <Zap size={16} />
                  <div>
                    <strong>{auto.name}</strong>
                    <span>
                      {eventLabel(auto.trigger)} • {(auto.conditions || []).length} conditions • {(auto.actions || []).length} steps • {auto.enabled ? "Enabled" : "Paused"}
                      {auto.paused_reason && !auto.enabled ? ` • ${auto.paused_reason}` : ""}
                      {auto.rate_limited_count ? ` • ${auto.rate_limited_count} runs dropped by the rate limit` : ""}
                    </span>
                    {!auto.enabled && !!auto.activation_errors?.length && <span style={{ color: "#fbbf24" }}>Cannot be enabled: {auto.activation_errors.join("; ")}</span>}
                  </div>
                  <button className="ghost-btn small" data-testid={`automation-edit-${auto.id}-button`} onClick={() => { setTab("automations"); setEditing(auto); }}><Pencil size={14} /> Edit</button>
                  <button className="ghost-btn small" data-testid={`automation-toggle-${auto.id}-button`} onClick={() => toggle(auto)}><Play size={14} /> {auto.enabled ? "Pause" : "Enable"}</button>
                  <button className="ghost-btn small" aria-label="Delete automation" onClick={() => remove(auto)}><Trash2 size={14} /></button>
                </div>
              ))}
              {!automations.length && <div className="empty-cell">No automations yet. Create your first automation above.</div>}
            </div>
          </section>
        </>
      )}
      {tab === "runs" && <RunLog api={api} showNotice={showNotice} automations={automations} />}
      {tab === "templates" && <MessageTemplates api={api} showNotice={showNotice} user={user} onChanged={load} />}
      {tab === "webhooks" && <WebhookEndpoints api={api} showNotice={showNotice} onChanged={load} />}
    </div>
  );
}
